import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PmoShell from "./PmoShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Search, Download, ArrowUpRight, Paperclip, Archive } from "lucide-react";

const fmtSize = (s) => (!s ? "—" : s > 1e6 ? `${(s / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(s / 1024))} KB`);

export default function PmoArtifacts() {
  const [arts, setArts] = useState(null);
  const [q, setQ] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    api.get("/pmo/artifacts").then(({ data }) => setArts(data)).catch(() => setArts([]));
  }, []);

  const shown = useMemo(() => {
    if (!arts) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return arts;
    return arts.filter((a) => `${a.original_filename} ${a.project_name} ${a.category || ""}`.toLowerCase().includes(needle));
  }, [arts, q]);

  const download = async (a) => {
    try {
      const { data } = await api.get(`/artifacts/${a.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = a.original_filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the file"); }
  };

  return (
    <PmoShell
      title="Artifacts"
      subtitle="Every document attached across all your projects — charters, plans, meeting recordings and deliverables in one place."
      testid="pmo-artifacts-page"
      actions={(
        <div className="relative w-full sm:w-72">
          <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search files, projects, categories…" className="pl-9" data-testid="pmo-artifacts-search" />
        </div>
      )}
    >
      {arts === null && <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>}
      {arts !== null && arts.length === 0 && (
        <div className="border border-dashed border-border rounded-lg p-12 text-center" data-testid="pmo-artifacts-empty">
          <Paperclip className="h-8 w-8 text-primary mx-auto mb-3" />
          <p className="text-muted-foreground">No artifacts yet — upload documents from any project's Artifacts tab and they'll appear here.</p>
        </div>
      )}
      {arts !== null && arts.length > 0 && (
        <div className="rounded-xl border border-border bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-2.5 font-bold">File</th>
                <th className="px-3 py-2.5 font-bold">Project</th>
                <th className="px-3 py-2.5 font-bold">Category</th>
                <th className="px-3 py-2.5 font-bold text-right">Version</th>
                <th className="px-3 py-2.5 font-bold text-right">Size</th>
                <th className="px-3 py-2.5 font-bold">Uploaded</th>
                <th className="px-3 py-2.5 font-bold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No artifacts match "{q}".</td></tr>
              )}
              {shown.map((a) => (
                <tr key={a.id} className="border-b border-border/60 last:border-0" data-testid={`pmo-artifact-${a.id}`}>
                  <td className="px-4 py-2 font-semibold max-w-[280px] truncate" title={a.original_filename}>{a.original_filename}</td>
                  <td className="px-3 py-2 text-muted-foreground max-w-[200px]">
                    <span className="inline-flex items-center gap-1.5 truncate">
                      {a.project_archived && <Archive className="h-3 w-3 shrink-0" />} {a.project_name}
                    </span>
                  </td>
                  <td className="px-3 py-2"><span className="text-xs font-semibold bg-secondary rounded-full px-2 py-0.5">{a.category || "Other"}</span></td>
                  <td className="px-3 py-2 text-right tabular-nums">v{a.version}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtSize(a.size)}</td>
                  <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => download(a)} title="Download" data-testid={`pmo-artifact-download-${a.id}`}>
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => nav(`/projects/${a.project_id}?tab=artifacts`)} title="Open in project" data-testid={`pmo-artifact-open-${a.id}`}>
                        <ArrowUpRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PmoShell>
  );
}
